import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { Invoice, InvoiceLine, InvoiceStatus, Project } from '../types';
import { getToday } from '../utils/dateUtils';

// ─── HELPERS ─────────────────────────────────────────────────────────────

function getFirstDayOfYear(): string {
  return `${new Date().getFullYear()}-01-01`;
}

/** Statut effectif : overdue est calculé automatiquement, pas stocké en base */
export function getEffectiveStatus(inv: { status: InvoiceStatus; due_date: string }): InvoiceStatus {
  if (inv.status === 'sent') {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(inv.due_date); due.setHours(0, 0, 0, 0);
    if (due < today) return 'overdue';
  }
  return inv.status;
}

/** Nombre de jours jusqu'à (positif) ou depuis (négatif) l'échéance */
export function getDueDays(dueDate: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate); due.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
}

/** Label J-x / Jour J / J+x avec couleur progressive */
export function getDueIndicator(days: number): { label: string; color: string } {
  if (days > 7) return { label: `J-${days}`, color: '#10b981' };
  if (days > 0) return { label: `J-${days}`, color: '#f59e0b' };
  if (days === 0) return { label: 'Jour J', color: '#ef4444' };
  if (days >= -7) return { label: `J+${Math.abs(days)}`, color: '#ef4444' };
  return { label: `J+${Math.abs(days)}`, color: '#991b1b' };
}

export const STATUS_CONFIG: Record<InvoiceStatus, { label: string; color: string; bg: string }> = {
  draft: { label: 'Brouillon', color: '#f59e0b', bg: '#fffbeb' },
  sent: { label: 'Envoyée', color: '#3b82f6', bg: '#eff6ff' },
  paid: { label: 'Payée', color: '#10b981', bg: '#ecfdf5' },
  overdue: { label: 'En retard', color: '#ef4444', bg: '#fef2f2' },
};

// ─── INTERFACES FORMULAIRE ──────────────────────────────────────────────

export interface FormLine {
  id?: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
}

export const EMPTY_LINE: FormLine = { description: '', quantity: 1, unit: 'u', unit_price: 0 };

// ─── FILE HELPERS ────────────────────────────────────────────────────────

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// ─── HOOK ────────────────────────────────────────────────────────────────

export function useInvoicingData() {
  // --- DATA ---
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  // --- FILTERS ---
  const [filterStatus, setFilterStatus] = useState<InvoiceStatus | 'all'>('all');
  const [filterProject, setFilterProject] = useState('all');
  const [dateStart, setDateStart] = useState(getFirstDayOfYear());
  const [dateEnd, setDateEnd] = useState(getToday());
  const [search, setSearch] = useState('');

  // --- FORM ---
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formClientName, setFormClientName] = useState('');
  const [formClientAddress, setFormClientAddress] = useState('');
  const [formClientEmail, setFormClientEmail] = useState('');
  const [formProjectId, setFormProjectId] = useState<string>('');
  const [formInvoiceDate, setFormInvoiceDate] = useState(getToday());
  const [formDueDate, setFormDueDate] = useState('');
  const [formTvaRate, setFormTvaRate] = useState(20);
  const [formNotes, setFormNotes] = useState('');
  const [formLines, setFormLines] = useState<FormLine[]>([{ ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);

  // --- EXPAND ---
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedLines, setExpandedLines] = useState<InvoiceLine[]>([]);

  // --- PDF DROP & OCR ---
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [signatureDetected, setSignatureDetected] = useState(false);
  const [validationMention, setValidationMention] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ─── FETCH DATA ────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invoicesRes, projectsRes] = await Promise.all([
        supabase
          .from('invoices')
          .select('*, project:projects(id, code, name), employee:employees!invoices_created_by_fkey(id, full_name)')
          .gte('invoice_date', dateStart)
          .lte('invoice_date', dateEnd)
          .order('invoice_date', { ascending: false }),
        supabase
          .from('projects')
          .select('id, code, name, is_active')
          .order('code')
      ]);

      if (invoicesRes.data) setInvoices(invoicesRes.data as Invoice[]);
      if (projectsRes.data) setProjects(projectsRes.data as Project[]);
    } catch (err) {
      console.error('[AdminInvoicing] fetchData error:', err);
    } finally {
      setLoading(false);
    }
  }, [dateStart, dateEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── EXPAND DETAIL ─────────────────────────────────────────────────────

  const handleExpand = useCallback(async (invoiceId: string) => {
    if (expandedId === invoiceId) {
      setExpandedId(null);
      setExpandedLines([]);
      return;
    }
    setExpandedId(invoiceId);
    const { data } = await supabase
      .from('invoice_lines')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('sort_order');
    setExpandedLines((data || []) as InvoiceLine[]);
  }, [expandedId]);

  // ─── FILTERING ─────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    return invoices.filter(inv => {
      if (filterStatus !== 'all' && getEffectiveStatus(inv) !== filterStatus) return false;
      if (filterProject !== 'all' && inv.project_id !== filterProject) return false;
      if (search) {
        const s = search.toLowerCase();
        const matchNum = inv.invoice_number?.toLowerCase().includes(s);
        const matchClient = inv.client_name?.toLowerCase().includes(s);
        const matchProject = inv.project?.code?.toLowerCase().includes(s) || inv.project?.name?.toLowerCase().includes(s);
        const matchNotes = inv.notes?.toLowerCase().includes(s);
        if (!matchNum && !matchClient && !matchProject && !matchNotes) return false;
      }
      return true;
    });
  }, [invoices, filterStatus, filterProject, search]);

  // ─── KPI ───────────────────────────────────────────────────────────────

  const kpi = useMemo(() => {
    const totalTTC = filtered.reduce((s, i) => s + (i.total_ttc || 0), 0);
    const pending = filtered.filter(i => getEffectiveStatus(i) === 'sent').reduce((s, i) => s + (i.total_ttc || 0), 0);
    const paid = filtered.filter(i => getEffectiveStatus(i) === 'paid').reduce((s, i) => s + (i.total_ttc || 0), 0);
    const overdue = filtered.filter(i => getEffectiveStatus(i) === 'overdue').reduce((s, i) => s + (i.total_ttc || 0), 0);
    return { totalTTC, pending, paid, overdue };
  }, [filtered]);

  // ─── FORM HELPERS ──────────────────────────────────────────────────────

  const resetForm = () => {
    setFormClientName('');
    setFormClientAddress('');
    setFormClientEmail('');
    setFormProjectId('');
    setFormInvoiceDate(getToday());
    setFormDueDate('');
    setFormTvaRate(20);
    setFormNotes('');
    setFormLines([{ ...EMPTY_LINE }]);
    setEditingId(null);
    setShowForm(false);
    setPdfFile(null);
    setExtractionError(null);
    setSignatureDetected(false);
    setValidationMention(null);
  };

  const computeDefaultDueDate = (invoiceDate: string): string => {
    const d = new Date(invoiceDate);
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  };

  const formTotalHT = useMemo(() => {
    return formLines.reduce((s, l) => s + l.quantity * l.unit_price, 0);
  }, [formLines]);

  const formTVA = formTotalHT * (formTvaRate / 100);
  const formTotalTTC = formTotalHT + formTVA;

  const updateLine = (idx: number, field: keyof FormLine, value: string | number) => {
    setFormLines(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const addLine = () => setFormLines(prev => [...prev, { ...EMPTY_LINE }]);

  const removeLine = (idx: number) => {
    if (formLines.length <= 1) return;
    setFormLines(prev => prev.filter((_, i) => i !== idx));
  };

  return {
    // data
    loading, invoices, projects, filtered, kpi,
    // filters
    filterStatus, setFilterStatus,
    filterProject, setFilterProject,
    dateStart, setDateStart,
    dateEnd, setDateEnd,
    search, setSearch,
    // form
    showForm, setShowForm,
    editingId, setEditingId,
    formClientName, setFormClientName,
    formClientAddress, setFormClientAddress,
    formClientEmail, setFormClientEmail,
    formProjectId, setFormProjectId,
    formInvoiceDate, setFormInvoiceDate,
    formDueDate, setFormDueDate,
    formTvaRate, setFormTvaRate,
    formNotes, setFormNotes,
    formLines, setFormLines,
    saving, setSaving,
    // expand
    expandedId, setExpandedId, expandedLines, handleExpand,
    // pdf
    pdfFile, setPdfFile,
    dragOver, setDragOver,
    extracting, setExtracting,
    extractionError, setExtractionError,
    signatureDetected, setSignatureDetected,
    validationMention, setValidationMention,
    fileInputRef,
    // form helpers
    resetForm, computeDefaultDueDate,
    formTotalHT, formTVA, formTotalTTC,
    updateLine, addLine, removeLine,
    // refresh
    fetchData,
  };
}
